
import React from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CookiePreferences } from '@/hooks/useCookieConsentLogic';

interface CookieBannerDetailedProps {
  preferences: CookiePreferences;
  setPreferences: (preferences: CookiePreferences) => void;
  onSavePreferences: () => void;
  onAcceptNecessary: () => void;
  onBack: () => void;
}

const cookieDetails = [
  {
    key: 'necessary' as const,
    title: 'Cookies nécessaires',
    description: 'Essentiels au fonctionnement du site (session, sécurité, panier)',
    duration: 'Session à 12 mois',
    required: true,
  },
  {
    key: 'analytics' as const,
    title: 'Cookies analytiques',
    description: 'Nous aident à comprendre comment vous utilisez le site (Google Analytics)',
    duration: '13 mois maximum',
    required: false,
  },
  {
    key: 'functional' as const,
    title: 'Cookies fonctionnels',
    description: 'Permettent de mémoriser vos préférences (langue, thème, paramètres)',
    duration: '12 mois maximum',
    required: false,
  },
  {
    key: 'marketing' as const,
    title: 'Cookies marketing',
    description: 'Utilisés pour la publicité ciblée et les réseaux sociaux',
    duration: '13 mois maximum',
    required: false,
  },
];

export const CookieBannerDetailed: React.FC<CookieBannerDetailedProps> = ({
  preferences,
  setPreferences,
  onSavePreferences,
  onAcceptNecessary,
  onBack,
}) => {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button 
          onClick={onBack} 
          className="p-2 hover:bg-muted rounded-full transition-colors"
          aria-label="Retour"
        >
          <ArrowLeft size={18} />
        </button>
        <h3 className="text-lg font-semibold">Préférences de cookies</h3>
      </div>
      
      <div className="space-y-4 mb-6">
        {cookieDetails.map((cookie) => (
          <div key={cookie.key} className="flex items-start justify-between gap-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-medium">{cookie.title}</h4>
                {cookie.required && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                    Obligatoire
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{cookie.description}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Durée : {cookie.duration}
              </p>
            </div>
            <Switch 
              checked={preferences[cookie.key]} 
              onCheckedChange={(checked) => 
                setPreferences({ ...preferences, [cookie.key]: checked })
              }
              disabled={cookie.required}
              aria-label={`Activer ${cookie.title}`}
            />
          </div>
        ))}
      </div>
      
      <div className="text-sm text-muted-foreground mb-4">
        Pour plus d'informations, consultez notre{' '}
        <Link to="/politique-cookies" className="underline text-primary hover:text-primary/80">
          politique de cookies
        </Link>.
      </div>
      
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onAcceptNecessary}>
          Cookies nécessaires uniquement
        </Button>
        <Button size="sm" onClick={onSavePreferences}>
          Enregistrer mes choix
        </Button>
      </div>
    </div>
  );
};
